/**
 * Polished LPU DE support-chat styles. Motion is limited to opacity and
 * transforms while the panel geometry stays fixed throughout streaming.
 */
export const WIDGET_STYLES = `
:host {
  --lpu-navy: hsl(217 56% 23%);
  --lpu-yellow: hsl(48 100% 51%);
  --lpu-ink: #182230;
  --lpu-muted: #697586;
  --lpu-line: #e6eaf0;
  --lpu-surface: #ffffff;
  --lpu-soft: #f5f7fa;
  --lpu-danger: #b42318;
  --lpu-shadow: 0 28px 80px rgb(15 36 66 / 20%), 0 8px 24px rgb(15 36 66 / 10%);
  all: initial;
  color: var(--lpu-ink);
  font-family: Inter, "Avenir Next", Roboto, "Segoe UI", Arial, sans-serif;
}

*, *::before, *::after { box-sizing: border-box; }
button, textarea { font: inherit; }
button { -webkit-tap-highlight-color: transparent; }

.lpu-chat-root {
  position: fixed;
  right: 22px;
  bottom: 22px;
  z-index: var(--lpu-z, 2147483000);
  color: var(--lpu-ink);
}
.lpu-chat-root[data-position="bottom-left"] { right: auto; left: 22px; }

.lpu-chat-launcher {
  position: absolute;
  right: 0;
  bottom: 0;
  display: flex;
  width: 164px;
  height: 58px;
  align-items: center;
  gap: 10px;
  padding: 7px 15px 7px 7px;
  border: 1px solid rgb(255 255 255 / 15%);
  border-radius: 19px;
  background: linear-gradient(145deg, hsl(217 59% 27%), hsl(217 58% 19%));
  box-shadow: 0 14px 35px rgb(15 36 66 / 28%);
  color: white;
  cursor: pointer;
  opacity: 1;
  transform: translate3d(0, 0, 0);
  transition: opacity 180ms ease, transform 220ms cubic-bezier(.2,.8,.2,1), box-shadow 180ms ease;
}
.lpu-chat-launcher:hover {
  box-shadow: 0 18px 42px rgb(15 36 66 / 34%);
  transform: translate3d(0, -3px, 0);
}
.lpu-chat-launcher-icon {
  display: grid;
  width: 43px;
  height: 43px;
  flex: 0 0 auto;
  place-items: center;
  border-radius: 14px;
  background: var(--lpu-yellow);
  color: var(--lpu-navy);
  box-shadow: inset 0 0 0 1px rgb(255 255 255 / 35%);
}
.lpu-chat-launcher-icon svg { width: 21px; height: 21px; }
.lpu-chat-launcher-copy { display: grid; min-width: 0; gap: 1px; text-align: left; }
.lpu-chat-launcher-copy strong { font-size: 12.5px; font-weight: 760; line-height: 1.2; }
.lpu-chat-launcher-copy small { color: rgb(255 255 255 / 68%); font-size: 10px; line-height: 1.2; }

.lpu-chat-launcher:focus-visible,
.lpu-chat-clear:focus-visible,
.lpu-chat-close:focus-visible,
.lpu-chat-send:focus-visible,
.lpu-chat-retry button:focus-visible,
.lpu-chat-handoff button:focus-visible,
.lpu-chat-handoff a:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--lpu-yellow) 82%, white);
  outline-offset: 3px;
}

.lpu-chat-panel {
  position: relative;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  width: min(376px, calc(100vw - 24px));
  height: min(620px, calc(100dvh - 36px));
  overflow: hidden;
  border: 1px solid rgb(18 38 68 / 11%);
  border-radius: 25px;
  background: var(--lpu-surface);
  box-shadow: var(--lpu-shadow);
  opacity: 0;
  pointer-events: none;
  transform: translate3d(0, 14px, 0) scale(.975);
  transform-origin: bottom right;
  visibility: hidden;
  transition:
    opacity 180ms ease,
    transform 240ms cubic-bezier(.16,1,.3,1),
    visibility 0s linear 240ms;
  contain: layout paint style;
}
.lpu-chat-panel::before {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  z-index: 3;
  height: 3px;
  background: linear-gradient(90deg, var(--lpu-yellow), #ffd94c 64%, transparent);
  content: "";
}
.lpu-chat-root[data-open="true"] .lpu-chat-panel {
  opacity: 1;
  pointer-events: auto;
  transform: translate3d(0, 0, 0) scale(1);
  visibility: visible;
  transition-delay: 0s;
}
.lpu-chat-root[data-open="true"] .lpu-chat-launcher {
  opacity: 0;
  pointer-events: none;
  transform: translate3d(0, 10px, 0) scale(.92);
}

.lpu-chat-header {
  position: relative;
  display: flex;
  min-height: 78px;
  align-items: center;
  gap: 12px;
  padding: 15px 15px 14px;
  overflow: hidden;
  background:
    radial-gradient(circle at 78% -30%, rgb(255 211 0 / 18%), transparent 45%),
    linear-gradient(145deg, hsl(217 58% 25%), hsl(217 58% 19%));
  color: white;
}
.lpu-chat-mark {
  position: relative;
  display: grid;
  width: 45px;
  height: 45px;
  flex: 0 0 auto;
  place-items: center;
  border: 1px solid rgb(255 255 255 / 24%);
  border-radius: 15px;
  background: linear-gradient(145deg, var(--lpu-yellow), #ffd84a);
  color: var(--lpu-navy);
  box-shadow: 0 7px 18px rgb(0 0 0 / 16%);
  font-size: 13px;
  font-weight: 850;
  letter-spacing: -.04em;
}
.lpu-chat-mark i {
  position: absolute;
  right: -2px;
  bottom: -2px;
  width: 12px;
  height: 12px;
  border: 2px solid var(--lpu-navy);
  border-radius: 50%;
  background: #42d392;
}
.lpu-chat-heading { min-width: 0; flex: 1; }
.lpu-chat-title {
  margin: 0;
  overflow: hidden;
  font-size: 14px;
  font-weight: 760;
  letter-spacing: -.01em;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.lpu-chat-subtitle {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 4px 0 0;
  color: rgb(255 255 255 / 70%);
  font-size: 10.5px;
  line-height: 1.2;
}
.lpu-chat-status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #65dfa4;
  box-shadow: 0 0 0 3px rgb(101 223 164 / 13%);
}
.lpu-chat-status-dot[data-error="true"] { background: #fda29b; }
.lpu-chat-clear,
.lpu-chat-close {
  display: grid;
  width: 34px;
  height: 34px;
  flex: 0 0 auto;
  place-items: center;
  border: 1px solid rgb(255 255 255 / 8%);
  border-radius: 11px;
  background: rgb(255 255 255 / 8%);
  color: rgb(255 255 255 / 80%);
  cursor: pointer;
  transition: background 150ms ease, color 150ms ease, transform 150ms ease;
}
.lpu-chat-clear:hover,
.lpu-chat-close:hover { background: rgb(255 255 255 / 15%); color: white; transform: translateY(-1px); }
.lpu-chat-clear[data-confirm="true"] {
  background: #b42318;
  color: white;
}
.lpu-chat-clear svg,
.lpu-chat-close svg { width: 16px; height: 16px; }

.lpu-chat-thread {
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  overflow-anchor: none;
  scroll-behavior: smooth;
  scrollbar-width: thin;
  scrollbar-color: #c8d0dc transparent;
  padding: 20px 14px 15px;
  background:
    radial-gradient(circle at 100% 0, rgb(255 211 0 / 7%), transparent 190px),
    linear-gradient(180deg, #f8fafc 0%, #f5f7fa 100%);
}
.lpu-chat-message {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  margin: 0 0 13px;
  animation: lpu-message-in 200ms cubic-bezier(.16,1,.3,1) both;
}
.lpu-chat-message[data-role="user"] { justify-content: flex-end; }
.lpu-chat-message-avatar {
  display: grid;
  width: 27px;
  height: 27px;
  flex: 0 0 auto;
  place-items: center;
  border: 1px solid rgb(18 38 68 / 9%);
  border-radius: 9px;
  background: var(--lpu-yellow);
  color: var(--lpu-navy);
  box-shadow: 0 3px 8px rgb(18 38 68 / 10%);
  font-size: 8px;
  font-weight: 850;
  letter-spacing: -.02em;
}
.lpu-chat-bubble {
  max-width: calc(86% - 35px);
  padding: 10px 12px;
  border: 1px solid rgb(18 38 68 / 7%);
  border-radius: 16px 16px 16px 5px;
  background: var(--lpu-surface);
  box-shadow: 0 4px 14px rgb(18 38 68 / 6%);
  color: var(--lpu-ink);
  font-size: 13px;
  letter-spacing: .001em;
  line-height: 1.52;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}
.lpu-chat-message[data-role="user"] .lpu-chat-bubble {
  max-width: 82%;
  border-color: transparent;
  border-radius: 16px 16px 5px 16px;
  background: linear-gradient(145deg, hsl(217 57% 27%), var(--lpu-navy));
  box-shadow: 0 5px 16px rgb(18 38 68 / 14%);
  color: white;
}
.lpu-chat-message[data-error="true"] .lpu-chat-bubble {
  border-color: #fecdca;
  background: #fff5f4;
  color: var(--lpu-danger);
}
.lpu-chat-message[data-streaming="true"] .lpu-chat-bubble::after {
  display: inline-block;
  width: 5px;
  height: 14px;
  margin-left: 3px;
  border-radius: 2px;
  background: var(--lpu-yellow);
  content: "";
  vertical-align: -2px;
  animation: lpu-caret 850ms steps(1) infinite;
}
.lpu-chat-typing {
  display: inline-flex;
  height: 18px;
  align-items: center;
  gap: 4px;
  padding-inline: 2px;
}
.lpu-chat-typing i {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #8e99a8;
  animation: lpu-dot 900ms ease-in-out infinite;
}
.lpu-chat-typing i:nth-child(2) { animation-delay: 120ms; }
.lpu-chat-typing i:nth-child(3) { animation-delay: 240ms; }

.lpu-chat-sources {
  display: flex;
  max-width: calc(86% - 35px);
  margin: -8px 0 10px 35px;
  gap: 6px;
}
.lpu-chat-source {
  display: inline-flex;
  min-height: 24px;
  align-items: center;
  padding: 0 8px;
  border: 1px solid rgb(18 38 68 / 10%);
  border-radius: 999px;
  background: rgb(255 255 255 / 75%);
  color: var(--lpu-navy);
  font-size: 9px;
  font-weight: 700;
  text-decoration: none;
  transition: border-color 140ms ease, background 140ms ease, transform 140ms ease;
}

.lpu-chat-suggestions {
  display: flex;
  max-width: calc(86% - 35px);
  margin: -5px 0 12px 35px;
  flex-wrap: wrap;
  gap: 6px;
}
.lpu-chat-suggestion {
  min-height: 28px;
  padding: 0 9px;
  border: 1px solid rgb(18 38 68 / 12%);
  border-radius: 999px;
  background: var(--lpu-surface);
  color: var(--lpu-navy);
  cursor: pointer;
  font-size: 10px;
  font-weight: 700;
  line-height: 1.2;
  text-align: left;
  transition: border-color 140ms ease, background 140ms ease, transform 140ms ease;
}
.lpu-chat-suggestion:hover {
  border-color: color-mix(in srgb, var(--lpu-yellow) 70%, transparent);
  background: color-mix(in srgb, var(--lpu-yellow) 9%, white);
  transform: translateY(-1px);
}

.lpu-chat-handoff {
  display: flex;
  max-width: calc(86% - 35px);
  margin: -6px 0 14px 35px;
  flex-wrap: wrap;
  gap: 7px;
}
.lpu-chat-handoff button,
.lpu-chat-handoff a {
  display: inline-flex;
  min-height: 32px;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  border: 1px solid color-mix(in srgb, var(--lpu-navy) 18%, transparent);
  border-radius: 999px;
  background: var(--lpu-surface);
  color: var(--lpu-navy);
  cursor: pointer;
  font-size: 11px;
  font-weight: 720;
  line-height: 1.2;
  text-decoration: none;
  transition: transform 150ms ease, box-shadow 150ms ease, background 150ms ease;
}
.lpu-chat-handoff button:first-child,
.lpu-chat-handoff a:first-child {
  border-color: transparent;
  background: var(--lpu-yellow);
  box-shadow: 0 4px 12px rgb(18 38 68 / 10%);
}
.lpu-chat-handoff button:hover,
.lpu-chat-handoff a:hover { transform: translateY(-1px); }
.lpu-chat-handoff button:disabled {
  cursor: wait;
  opacity: .65;
  transform: none;
}
.lpu-chat-handoff--connected { margin-top: -7px; }
.lpu-chat-source:hover {
  border-color: rgb(18 38 68 / 22%);
  background: white;
  transform: translateY(-1px);
}
.lpu-chat-retry {
  display: flex;
  min-height: 28px;
  align-items: center;
  gap: 8px;
  margin: -7px 0 14px 35px;
  color: var(--lpu-danger);
  font-size: 10px;
}
.lpu-chat-retry button {
  min-height: 28px;
  padding: 0 10px;
  border: 1px solid #fecdca;
  border-radius: 999px;
  background: #fff;
  color: var(--lpu-danger);
  cursor: pointer;
  font-weight: 750;
}
.lpu-chat-retry button:hover {
  background: #fff5f4;
}

.lpu-chat-composer-shell {
  padding: 11px 12px 10px;
  border-top: 1px solid var(--lpu-line);
  background: rgb(255 255 255 / 96%);
  box-shadow: 0 -8px 24px rgb(18 38 68 / 4%);
}
.lpu-chat-composer-row {
  display: flex;
  align-items: flex-end;
  gap: 7px;
  padding: 5px 5px 5px 12px;
  border: 1px solid #dce2e9;
  border-radius: 17px;
  background: #f8fafc;
  transition: border-color 150ms ease, box-shadow 150ms ease, background 150ms ease;
}
.lpu-chat-composer-row:focus-within {
  border-color: color-mix(in srgb, var(--lpu-navy) 42%, white);
  background: white;
  box-shadow: 0 0 0 4px rgb(18 38 68 / 6%);
}
.lpu-chat-composer {
  width: 100%;
  min-height: 36px;
  max-height: 92px;
  padding: 8px 0 6px;
  resize: none;
  overflow-y: auto;
  border: 0;
  outline: 0;
  background: transparent;
  color: var(--lpu-ink);
  font-size: 13px;
  line-height: 1.45;
}
.lpu-chat-composer::placeholder { color: #8b96a6; }
.lpu-chat-composer:disabled { cursor: wait; }
.lpu-chat-send {
  display: grid;
  width: 36px;
  height: 36px;
  flex: 0 0 auto;
  place-items: center;
  border: 0;
  border-radius: 12px;
  background: var(--lpu-yellow);
  box-shadow: 0 5px 12px rgb(190 145 0 / 18%);
  color: var(--lpu-navy);
  cursor: pointer;
  transition: opacity 140ms ease, transform 150ms cubic-bezier(.2,.8,.2,1), box-shadow 150ms ease;
}
.lpu-chat-send:hover:not(:disabled) { box-shadow: 0 7px 16px rgb(190 145 0 / 24%); transform: translateY(-2px); }
.lpu-chat-send:active:not(:disabled) { transform: translateY(0) scale(.96); }
.lpu-chat-send:disabled { cursor: default; opacity: .38; box-shadow: none; }
.lpu-chat-send svg { width: 17px; height: 17px; }
.lpu-chat-composer-meta {
  display: flex;
  min-height: 12px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 7px 2px 0;
}
.lpu-chat-note {
  margin: 0;
  color: #8a94a3;
  font-size: 8.5px;
  letter-spacing: .015em;
  line-height: 1.3;
  text-align: center;
}
.lpu-chat-character-count {
  margin-left: auto;
  color: #687487;
  font-size: 9px;
  font-variant-numeric: tabular-nums;
  line-height: 1;
}

.lpu-chat-character-count[data-over-limit="true"] {
  color: #b42318;
  font-weight: 700;
}
.lpu-chat-character-count[hidden] { display: none; }

@keyframes lpu-message-in {
  from { opacity: 0; transform: translate3d(0, 7px, 0) scale(.99); }
}
@keyframes lpu-caret { 50% { opacity: 0; } }
@keyframes lpu-dot {
  0%, 60%, 100% { opacity: .4; transform: translateY(0); }
  30% { opacity: 1; transform: translateY(-3px); }
}

@media (max-width: 520px) {
  .lpu-chat-root,
  .lpu-chat-root[data-position="bottom-left"] {
    right: 8px;
    bottom: max(8px, env(safe-area-inset-bottom));
    left: 8px;
  }
  .lpu-chat-launcher {
    right: 4px;
    bottom: 4px;
    width: 56px;
    height: 56px;
    padding: 6px;
    border-radius: 18px;
  }
  .lpu-chat-launcher-icon { width: 44px; height: 44px; }
  .lpu-chat-launcher-copy { display: none; }
  .lpu-chat-panel {
    width: 100%;
    height: min(680px, calc(100dvh - 16px));
    border-radius: 23px;
    transform-origin: bottom center;
  }
}

@media (prefers-reduced-motion: reduce) {
  .lpu-chat-panel,
  .lpu-chat-launcher,
  .lpu-chat-message,
  .lpu-chat-send,
  .lpu-chat-source {
    animation: none !important;
    scroll-behavior: auto;
    transition-duration: 1ms !important;
  }
  .lpu-chat-typing i,
  .lpu-chat-message[data-streaming="true"] .lpu-chat-bubble::after {
    animation: none !important;
  }
}
`;
